import React, { useState, useCallback, Suspense } from "react";

import { Router } from "@reach/router";

import VideoPlayer from "./components/VideoPlayer";
import Content from "./components/Content";
import Spinner from "./components/Spinner";

import { gql, useQuery, useMutation } from "@apollo/client";

import { findVideo } from "./components/admin/utils";
import { getContentQuery } from "./queries/queries";
import ReactGA from "react-ga";

function App(props) {
  if (process.env.REACT_APP_GA_TRACKING_ID) {
    ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);
    ReactGA.pageview(window.location.pathname + window.location.search);
  }
  const isCompletedVideoMutation = gql`
    mutation updateVideo($id: ID!, $isCompleted: Boolean!) {
      updateVideo(id: $id, isCompleted: $isCompleted) {
        id
        isCompleted
      }
    }
  `;

  const [isCompletedVideo] = useMutation(isCompletedVideoMutation);

  const { loading, error, data } = useQuery(getContentQuery, {});

  const [progress, setProgress] = useState(0);

  const [state, setState] = useState({
    idCurrentVideo: null,
    idCurrentSection: null,
    previousVideo: null,
    previousSection: null,
    nextVideo: null,
    nextSection: null,
    videoUrl: null
  });

  const handleVideoSelect = (e, sections) => {
    let indexSelectedSection = null;
    let indexSelectedVideo = null;
    const idSelectedVideo = e.currentTarget.value;

    sections.forEach((section, iSection) => {
      section.videos.forEach((video, iVideo) => {
        if (video.id === e.currentTarget.value) {
          indexSelectedSection = iSection;
          indexSelectedVideo = iVideo;
        }
      });
    });

    const url =
      sections[indexSelectedSection].videos[indexSelectedVideo].videoUrl;

    const idSelectedSection = sections[indexSelectedSection].id;

    //Setting prev and next lesson

    let { idPrevVideo, idPrevSection } = getPreviousId(
      sections,
      idSelectedVideo
    );

    let { idNextVideo, idNextSection } = getNextId(sections, idSelectedVideo);

    setState({
      idCurrentVideo: idSelectedVideo,
      idCurrentSection: idSelectedSection,
      previousVideo: idPrevVideo,
      previousSection: idPrevSection,
      nextVideo: idNextVideo,
      nextSection: idNextSection,
      videoUrl: url
    });
  };

  const autoAdvanceVideo = useCallback(
    sections => {
      const { idNextVideo, idNextSection } = getNextId(
        sections,
        state.idCurrentVideo
      );

      const idSelectedVideo = idNextVideo;
      const idSelectedSection = idNextSection;

      if (idSelectedVideo) {
        const video = findVideo(idSelectedVideo, sections);
        const url = video.videoUrl;

        //Setting prev and next lesson

        let { idPrevVideo, idPrevSection } = getPreviousId(
          sections,
          idSelectedVideo
        );

        let { idNextVideo, idNextSection } = getNextId(
          sections,
          idSelectedVideo
        );

        setState({
          idCurrentVideo: idSelectedVideo,
          idCurrentSection: idSelectedSection,
          previousVideo: idPrevVideo,
          previousSection: idPrevSection,
          nextVideo: idNextVideo,
          nextSection: idNextSection,
          videoUrl: url
        });
      }
    },
    [state.idCurrentVideo]
  );

  const getNextId = (sections, idCurrentVideo) => {
    let indexCurrentVideo = null;
    let indexCurrentSection = null;
    let indexNextVideo = null;
    let indexNextSection = null;
    let idNextVideo = null;
    let idNextSection = null;

    if (idCurrentVideo) {
      sections.forEach((section, iSection) => {
        section.videos.forEach((video, iVideo) => {
          if (video.id === idCurrentVideo) {
            indexCurrentVideo = iVideo;
            indexCurrentSection = iSection;
          }
        });
      });

      if (
        indexCurrentSection >= sections.length - 1 &&
        indexCurrentVideo >= sections[indexCurrentSection].videos.length - 1
      ) {
        indexNextSection = indexCurrentSection;
        indexNextVideo = -1;
      } else if (
        indexCurrentVideo ===
        sections[indexCurrentSection].videos.length - 1
      ) {
        indexNextSection = indexCurrentSection + 1;
        indexNextVideo = 0;
      } else {
        indexNextSection = indexCurrentSection;
        indexNextVideo = indexCurrentVideo + 1;
      }

      idNextSection = sections[indexNextSection].id;
      if (indexNextVideo !== -1)
        idNextVideo = sections[indexNextSection].videos[indexNextVideo].id;
    }

    return { idNextVideo, idNextSection };
  };

  const previousVideo = useCallback(
    sections => {
      let { idPrevVideo, idPrevSection } = getPreviousId(
        sections,
        state.idCurrentVideo
      );

      const idSelectedVideo = idPrevVideo;
      const idSelectedSection = idPrevSection;

      if (idPrevVideo) {
        const previousVideo = findVideo(idSelectedVideo, sections);
        const url = previousVideo.videoUrl;

        let { idPrevVideo, idPrevSection } = getPreviousId(
          sections,
          idSelectedVideo
        );

        let { idNextVideo, idNextSection } = getNextId(
          sections,
          idSelectedVideo
        );

        setState({
          idCurrentVideo: idSelectedVideo,
          idCurrentSection: idSelectedSection,
          previousVideo: idPrevVideo,
          previousSection: idPrevSection,
          nextVideo: idNextVideo,
          nextSection: idNextSection,
          videoUrl: url
        });
      }
    },
    [state.idCurrentVideo]
  );

  const getPreviousId = (sections, idCurrentVideo) => {
    let indexCurrentVideo = null;
    let indexCurrentSection = null;
    let indexPrevVideo = null;
    let indexPrevSection = null;
    let idPrevVideo = null;
    let idPrevSection = null;

    sections.forEach((section, iSection) => {
      section.videos.forEach((video, iVideo) => {
        if (video.id === idCurrentVideo) {
          indexCurrentVideo = iVideo;
          indexCurrentSection = iSection;
        }
      });
    });

    if (indexCurrentSection === 0 && indexCurrentVideo === 0) {
      indexPrevVideo = indexCurrentVideo - 1;

      indexPrevSection = indexCurrentSection;
    } else if (indexCurrentVideo === 0) {
      indexPrevSection = indexCurrentSection - 1;

      indexPrevVideo = sections[indexPrevSection].videos.length - 1;
    } else {
      indexPrevSection = indexCurrentSection;

      indexPrevVideo = indexCurrentVideo - 1;
    }

    if (indexPrevSection >= 0) idPrevSection = sections[indexPrevSection].id;
    if (indexPrevVideo >= 0)
      idPrevVideo = sections[indexPrevSection].videos[indexPrevVideo].id;

    return { idPrevVideo, idPrevSection };
  };

  const handleChangeVideoPath = useCallback((slug, sections) => {
    let url = null;

    let idSelectedSection = null;
    let idSelectedVideo = null;
    let indexSelectedVideo = null;
    let indexSelectedSection = null;

    sections.forEach((section, iSection) => {
      section.videos.forEach((video, iVideo) => {
        if (video.slug === slug) {
          indexSelectedSection = iSection;
          indexSelectedVideo = iVideo;
          url = video.videoUrl;
        }
      });
    });

    idSelectedVideo =
      sections[indexSelectedSection].videos[indexSelectedVideo].id;

    idSelectedSection = sections[indexSelectedSection].id;

    let { idPrevVideo, idPrevSection } = getPreviousId(
      sections,
      idSelectedVideo
    );

    let { idNextVideo, idNextSection } = getNextId(sections, idSelectedVideo);

    setState({
      idCurrentVideo: idSelectedVideo,
      idCurrentSection: idSelectedSection,
      previousVideo: idPrevVideo,
      previousSection: idPrevSection,
      nextVideo: idNextVideo,
      nextSection: idNextSection,
      videoUrl: url
    });
  }, []);

  const updateVideo = idVideo => {
    const id = idVideo;
    isCompletedVideo({
      variables: {
        id,
        isCompleted: true
      }
    });
  };

  const calculeProgress = sections => {
    let completedLessons = 0;
    let totalLessons = 0;

    sections.forEach(section =>
      section.videos.forEach(lesson => {
        totalLessons++;
        if (lesson.isCompleted === true) {
          completedLessons++;
        }
      })
    );

    const progress =
      Math.round((completedLessons / totalLessons) * 100).toString() + "%";

    setProgress(progress);
  };

  return (
    <Suspense fallback={"Loading"}>
      <div className="App">
        {loading && <Spinner />}
        {error && <p>Error ...</p>}
        {data && (
          <Router>
            <Content
              path={`/videos`}
              handleVideoSelect={handleVideoSelect}
              videoUrl={state.videoUrl}
              idCurrentSection={state.idCurrentSection}
              idCurrentVideo={state.idCurrentVideo}
              courseSlug={"videos"}
              progress={progress}
              title={"Video Player"}
              sections={data.sections}
              setState={setState}
            >
              <VideoPlayer
                path=":videoSlug"
                videoUrl={state.videoUrl}
                sections={data.sections}
                idCurrentVideo={state.idCurrentVideo}
                prevVideo={state.previousVideo}
                nextVideo={state.nextVideo}
                nextSection={state.nextSection}
                setState={setState}
                previousVideo={previousVideo}
                autoAdvanceVideo={autoAdvanceVideo}
                handleChangeVideoPath={handleChangeVideoPath}
                calculeProgress={calculeProgress}
                changes={updateVideo}
              />
            </Content>
          </Router>
        )}
      </div>
    </Suspense>
  );
}

export default App;
